import { Layout as AntdLayout, notification } from 'antd';
import { Auth, Hub } from 'aws-amplify';
import { IAuthenticatorProps } from 'aws-amplify-react/lib/Auth/Authenticator';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router';
import { AccountStatus } from '../enums';

import { logout, setAccountDisabled, updateExportVideoState } from '../actions';
import Header from '../components/Header';
import WrappedUpgradeModal from '../components/WrappedUpgradeModal';
import AnalyticsModal from '../components/AnalyticsModal';
import useUser from '../hooks/useUser';
import Authentication from './Authentication';
import GiideList from './GiideList';
import GiidePage from './GiidePage';
import Account from './Account';
import CustomPayment from './CustomPayment';
import BlockMobileModal from '../components/BlockMobileModal';

import { IState } from '../graphql/types';
import { isLocalhost } from '../components/utils';
import usePlanSubscription from '../hooks/useSubscription/usePlanSubscription';
import PaymentCompleteModal from '../components/Upgrade/PaymentCompleteModal';
import ExportVideoStartModal from '../components/ExportVideo/ExportVideoStartModal';
import ExportVideoProgress from '../components/ExportVideo/ExportVideoProgress';
import useExportVideo from '../hooks/useSubscription/useExportVideo';
import WelcomePage from './WelcomePage';
import GiidePasswordModal from '../components/PasswordProtection/GiidePasswordModal';

const exportVideoNotificationKey = 'exportKey';

const Layout: React.FC<IAuthenticatorProps> = props => {
  const { user, refetch } = useUser();
  const { subscribe } = usePlanSubscription();
  const accountDisabled = useSelector<IState, boolean>(
    state => state.accountDisabled
  );
  const [loadingUser, setLoadingUser] = React.useState(false);

  const [notificationAPI, notificationContextHolder] =
    notification.useNotification();

  useEffect(() => {
    // subscribe to plan change events, need to have a user to successfully subscribe
    if (user) {
      subscribe();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!window.location.pathname.startsWith('/studio/login')) {
      Auth.currentAuthenticatedUser()
        .then(user => refetch(user))
        .catch(() => push('/login'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { pathname } = useLocation();
  const { push } = useHistory();
  const shouldStartWithWelcomePage =
    pathname.substring(0, 6) !== '/login' &&
    pathname.substring(0, 7) !== '/signup';
  const dispatch = useDispatch();
  if (props.authState === 'verifyContact' && props.onStateChange) {
    props.onStateChange('signedIn', props.authData);
  }
  const [layoutClass, setLayoutClass] = React.useState<string>(
    'App-background-white'
  );
  // const [showWelcomePage, setShowWelcomePage] = React.useState<boolean>(true);
  const addFeedcardModalOpened = useSelector<IState, boolean>(
    state => !!state.addFeedcardModalOpened
  );
  const ownRecordingStarted = useSelector<IState, boolean | undefined>(
    state => state.ownRecordingStarted
  );

  const { exportState } = useExportVideo();

  useEffect(() => {
    const isFinished = exportState.downloadLink;
    const isError = exportState.error;

    if (exportState.id) {
      notificationAPI.open({
        key: exportVideoNotificationKey,
        message: null,
        description: <ExportVideoProgress exportState={exportState} />,
        placement: 'bottomRight',
        duration: 0,
        className: 'export-video-notification',
        icon: null,
        closeIcon: isFinished || isError ? undefined : <></>,
        onClose: () =>
          dispatch(
            updateExportVideoState({
              id: undefined,
              title: undefined,
              error: undefined,
              status: undefined,
              downloadLink: undefined,
              modal: undefined,
              requestedGiideId: undefined,
              requestedGiideTitle: undefined,
              progress: undefined,
            })
          ),
      });
    }
  }, [dispatch, exportState, notificationAPI]);

  React.useEffect(() => {
    const authListener = event => {
      if (event.payload.event === 'signOut') {
        dispatch(logout());
        if (props.authState === 'loading' && props.onStateChange) {
          props.onStateChange('signIn', {});
        }
      }
    };

    Hub.listen('auth', authListener);
    return () => {
      Hub.remove('auth', authListener);
    };
  }, [dispatch, props]);

  React.useEffect(() => {
    const userListener = event => {
      if (event.payload.event === 'loadingUser') {
        setLoadingUser(true);
      } else {
        setLoadingUser(false);
      }
    };

    Hub.listen('user', userListener);

    return () => {
      Hub.remove('user', userListener);
    };
  }, []);

  React.useEffect(() => {
    if (pathname.substring(1, 6) === 'admin') {
      setLayoutClass('App-background');
    } else {
      setLayoutClass('App-background-white ant-layout-content');
    }
  }, [setLayoutClass, pathname]);

  const isGiideView = React.useMemo(() => {
    return pathname.includes('/giide/');
  }, [pathname]);

  useEffect(() => {
    if (
      user?.plan?.accountStatus === AccountStatus.freeTrialExpired ||
      user?.plan?.accountStatus === AccountStatus.paddleCanceled ||
      user?.plan?.accountStatus === AccountStatus.paddleDeleted
    ) {
      dispatch(setAccountDisabled(true));
    } else {
      dispatch(setAccountDisabled(false));
    }
  }, [user, dispatch]);

  if (
    props.authState !== 'loading' &&
    !props.authData &&
    shouldStartWithWelcomePage
  ) {
    if (isLocalhost) return <Redirect to="/login" />;
    else window.location.assign(`${window.location.origin}/signin`);
  }

  return (
    <AntdLayout
      className={`${
        addFeedcardModalOpened || ownRecordingStarted ? 'noPointerEvenets' : ''
      } ${isGiideView ? 'giideView' : ''}`}
    >
      {notificationContextHolder}
      <BlockMobileModal />
      <WrappedUpgradeModal />
      <AnalyticsModal />

      <GiidePasswordModal />

      <PaymentCompleteModal />

      {!['/login'].includes(pathname) && (
        <Header
          authState={props.authState}
          onStateChange={props.onStateChange}
          user={user}
        />
      )}

      <WelcomePage
        loading={loadingUser || (props.authState === 'signedIn' && !user)}
      />

      <ExportVideoStartModal />

      <AntdLayout className={layoutClass}>
        <Switch>
          {!accountDisabled && (
            <Route path="/giide/:id" component={GiidePage} />
          )}

          <Route
            exact
            path="/signup"
            children={<Authentication {...{ ...props, authState: 'signUp' }} />}
          />

          <Route exact path="/login" children={<Authentication {...props} />} />

          <Route path="/account" component={Account} />

          <Route path="/payment/custom-pro-plan" component={CustomPayment} />

          <Route
            path="/payment/custom-creator-plan"
            component={CustomPayment}
          />

          <Route path="/payment/plus-plan" component={CustomPayment} />

          <Route path="/" component={GiideList} />
        </Switch>
      </AntdLayout>
    </AntdLayout>
  );
};

export default Layout;
